import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface EnterpriseCustomField {
  id: string;
  field_key: string;
  field_label: string;
  field_type: "text" | "textarea" | "number" | "select" | "checkbox" | "date" | string;
  applies_to: "proposal" | "contract" | "both" | string;
  is_required: boolean;
  help_text: string | null;
  options: string[];
  display_order: number;
}

/**
 * Fetches the current user's enterprise custom fields scoped to the given applies_to context.
 * Returns an empty array for non-enterprise users (RLS naturally filters by auth.uid()).
 * Includes fields with applies_to === 'both'.
 */
export function useEnterpriseCustomFields(appliesTo: "proposal" | "contract") {
  const { user } = useAuth();
  const [fields, setFields] = useState<EnterpriseCustomField[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setFields([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("enterprise_custom_fields")
        .select("id, field_key, field_label, field_type, applies_to, is_required, help_text, options, display_order")
        .eq("user_id", user.id)
        .in("applies_to", [appliesTo, "both"])
        .order("display_order", { ascending: true });
      if (!cancelled) {
        if (error) {
          console.error("Failed to load custom fields:", error);
          setFields([]);
        } else {
          setFields(
            (data || []).map((f: any) => ({
              ...f,
              options: Array.isArray(f.options) ? f.options : [],
            }))
          );
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, appliesTo]);

  return { fields, loading, hasFields: fields.length > 0 };
}
